import React from 'react';
import { css } from '@emotion/react';
import StarEmpty from '../../../images/svg/star-empty.svg';
import StarFill from '../../../images/svg/star-fill.svg';
import Skeleton from '../../primitives/skeleton';
import { breakpoints } from '../../primitives/tokens';
import { AVERAGE_RATE_THRESHOLD } from '../../../constants';

interface StarRatingProps {
    rating: number;
    smallSize?: boolean;
}

interface RatingSummaryProps {
    rating: number;
    count: number;
    smallSize?: boolean;
}

const styles = {
    starContainer: css({
        position: 'relative',
    }),
    starOverlay: css({
        position: 'absolute',
        top: '0',
        overflow: 'hidden',
        width: '50%',
    }),
    ratingContainer: css({
        display: 'flex',
        gap: '4px',
        alignItems: 'center',
        flexDirection: 'row',
    }),
    ratingContainerSmall: (smallSize: boolean) => css({
        display: 'flex',
        gap: '4px',
        alignItems: 'center',
        flexDirection: 'row',
        [breakpoints.tb]: {
            flexDirection: smallSize ? 'column' : 'row',
        },
    }),
    starsWrapper: css({
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        gap: '4px',
    }),
    ratingText: (smallSize: boolean) => css({
        marginTop: '4px',
        fontWeight: 700,
        fontSize: smallSize ? '12px' : undefined,
        lineHeight: smallSize ? '16px' : undefined,
    }),
    reviewsText: (smallSize: boolean) => css({
        paddingLeft: '4px',
        fontSize: '12px',
        lineHeight: '16px',
        textAlign: 'left',
        marginTop: '4px',
        whiteSpace: 'nowrap',
        [breakpoints.tb]: {
            marginTop: smallSize ? '0' : '4px',
        },
    }),
};

const StarRating: React.FC<StarRatingProps> = ({ rating, smallSize = false }) => (
    <>
        {[...Array(5).keys()].map(i => (
            <div css={styles.starContainer} key={i}>
                <StarEmpty width={smallSize ? 15 : 22} />
                {i < Math.ceil(rating) && (
                    <div
                        css={styles.starOverlay}
                        style={{
                            width: (() => {
                                if (i < Math.floor(rating)) {
                                    return '100%';
                                }
                                return `${Math.round(100 * (rating % 1))}%`;
                            })(),
                        }}
                    >
                        <StarFill width={smallSize ? 15 : 22} />
                    </div>
                )}
            </div>
        ))}
    </>
);

const RatingSummary: React.FC<RatingSummaryProps> = ({
    rating,
    count,
    smallSize = false,
}) => {
    if ((typeof rating !== 'number' && typeof count !== 'number') || AVERAGE_RATE_THRESHOLD > rating) {
        return null;
    }

    return (
        <Skeleton>
            <div css={styles.ratingContainerSmall(smallSize)}>
                <div css={styles.starsWrapper}>
                    <StarRating smallSize={smallSize} rating={rating} />
                    <span css={styles.ratingText(smallSize)}>
                        {rating.toFixed(1)}
                    </span>
                </div>
                <div css={styles.reviewsText(smallSize)}>
                    (
                    {count}
                    {' '}
                    reviews)
                </div>
            </div>
        </Skeleton>
    );
};

export default RatingSummary;
